import {
  Alert,
  Box,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useState } from "react";
import PublicOrganizationAutoComplete from "../../components/OrganizationAutoComplete/PublicOrganizationAutoComplete";
import {
  DriverVehicleInformationVehicleTypes,
  filterFormErrorObject,
  isAnyKeyValueTrue,
  mapObjectValues,
  objectHasKeys,
  ServiceDriverTypes,
  validateDateOfBirth,
  validateDrivingLicenseNumber,
  validateEmail,
  validatePhoneNumber,
  validatePostalCode,
  validatePresence,
} from "../../helpers/validationHelpers";
import useFormFieldsBlurState from "../../hooks/useFormFieldsBlurState";
import ImageUploadSection from "../../components/ImageUploadSection/ImageUploadSection";
import ErrorHelperText from "../../components/ErrorHelperText/ErrorHelperText";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import PhoneNumberTextField from "../../components/PhoneNumberTextField/PhoneNumberTextField";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

import { uploadAssetToCloudinary } from "../../utils/cloudinary";
import Button from "../../components/Button/Button";
import { createInitialDriverAccount } from "../../hooks/useDrivers";
import DriverProfileDetailsForm from "./DriverProfileDetailsForm";
import {
  useOrganizationPublicFacingBillingSettings,
  webhookValidateOrganizationUserSubmission,
} from "../../hooks/useOrganizations";
import DriverLicenseAndInsuranceNumberForm from "./DriverLicenseAndInsuranceNumberForm";
import DriverVehicleDetailsForm from "./DriverVehicleDetailsForm";
import DriverDocumentUploadForm from "./DriverDocumentUploadForm";
import CriminalRecordCheckConsentForm from "./CriminalRecordCheckConsentForm";
import {
  convertDriverUsingOwnVehicleOptionToBoolean,
  DriverUsingOwnVehicleOptions,
} from "./constants";
import _ from "lodash";

const DriverSignup = () => {
  // submit state
  const [page, setPage] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [creationValidationLoading, setCreationValidationLoading] =
    useState(false);
  // form fields

  // page 1
  const [organizationSearchText, setOrganizationSearchText] = useState("");
  const [organization, setOrganization] = useState<any>(null);
  const currentDate = moment(new Date(), true).set({ seconds: 0 });
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [avatar, setAvatar] = useState("");
  const [avatarUploadLoading, setAvatarUploadLoading] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState(moment());
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [driverPostalCode, setDriverPostalCode] = useState("");
  // page 2
  const [drivingLicenseNumber, setDrivingLicenseNumber] = useState("");
  const [insurancePolicyNumber, setInsurancePolicyNumber] = useState("");
  const [driverUsingOwnVehicle, setDriverUsingOwnVehicle] =
    useState<boolean>(false);
  const [driverUsingOrganizationVehicle, setDriverUsingOrganizationVehicle] =
    useState<boolean>(false);
  // page 3
  const {
    data: billingSettingsData,
    isLoading: isOrganizationBillingSettingsLoading,
  } = useOrganizationPublicFacingBillingSettings(organization?.id);
  const currentYear = new Date().getFullYear();
  const [vehicleType, setVehicleType] = useState(
    DriverVehicleInformationVehicleTypes.pickUpTruck
  );
  const [licensePlateNumber, setLicensePlateNumber] = useState("");
  const [makeOfCar, setMakeOfCar] = useState("");
  const [carColor, setCarColor] = useState("");
  const [carYear, setCarYear] = useState(currentYear);
  const [selectedEquipment, setSelectedEquipment] = useState({
    "Ratchet Straps": false,
    "Moving Blankets": false,
    Tarp: false,
    Dolly: false,
    Trailer: false,
    Other: false,
    None: false,
  });
  const [otherEquipmentDetails, setOtherEquipmentDetails] = useState("");
  // page 4
  const [drivingLicenseFrontUpload, setDrivingLicenseFrontUpload] =
    useState("");
  const [
    drivingLicenseFrontUploadLoading,
    setDrivingLicenseFrontUploadLoading,
  ] = useState(false);
  const [drivingLicenseBackUpload, setDrivingLicenseBackUpload] = useState("");
  const [drivingLicenseBackUploadLoading, setDrivingLicenseBackUploadLoading] =
    useState(false);
  const [vehicleSideUpload, setVehicleSideUpload] = useState("");
  const [vehicleSideUploadLoading, setVehicleSideUploadLoading] =
    useState(false);
  // const [vehicleCargoBedUpload, setVehicleCargoBedUpload] = useState("");
  // const [vehicleCargoBedUploadLoading, setVehicleCargoBedUploadLoading] =
  //   useState(false);
  const [vehicleProofOfOwnershipUpload, setVehicleProofOfOwnershipUpload] =
    useState("");
  const [
    vehicleProofOfOwnershipUploadLoading,
    setVehicleProofOfOwnershipUploadLoading,
  ] = useState(false);
  const [vehicleInsuranceUpload, setVehicleInsuranceUpload] = useState("");
  const [vehicleInsuranceUploadLoading, setVehicleInsuranceUploadLoading] =
    useState(false);
  // page 5
  const [criminalRecordSubmitted, setCriminalRecordSubmitted] = useState(false);

  const resetFormFields = () => {
    // page 1
    setOrganizationSearchText("");
    setOrganization(null);
    setFirstName("");
    setLastName("");
    setPhoneNumber("");
    setAvatar("");
    setEmail("");
    setDateOfBirth(moment());
    setAddress("");
    setCity("");
    setProvince("");
    setDriverPostalCode("");
    // page 2
    setDrivingLicenseNumber("");
    setInsurancePolicyNumber("");
    setDriverUsingOwnVehicle(false);
    setDriverUsingOrganizationVehicle(false);
    // page 3
    setVehicleType(DriverVehicleInformationVehicleTypes.pickUpTruck);
    setLicensePlateNumber("");
    setMakeOfCar("");
    setCarColor("");
    setCarYear(currentYear);
    setSelectedEquipment({
      "Ratchet Straps": false,
      "Moving Blankets": false,
      Tarp: false,
      Dolly: false,
      Trailer: false,
      Other: false,
      None: false,
    });
    setOtherEquipmentDetails("");
    // page 4
    setDrivingLicenseFrontUpload("");
    setDrivingLicenseBackUpload("");
    setVehicleSideUpload("");
    setVehicleProofOfOwnershipUpload("");
    setVehicleInsuranceUpload("");
    // page 5
    setCriminalRecordSubmitted(false);
    // finally, reset page state
    setPage(0);
  };

  const validateNewDriverCreate = async () => {
    setCreationValidationLoading(true);
    try {
      await webhookValidateOrganizationUserSubmission({
        organizationId: organization?.id,
        email,
        phoneNumber,
        dateOfBirth: dateOfBirth.toDate(),
      });
      setPage((prev) => prev + 1);
    } catch (error: any) {
      setShowError(true);
      setErrorMessage(error);
    } finally {
      setCreationValidationLoading(false);
    }
  };

  const formatSelectedEquipment = (
    selectedEquipment: Record<string, boolean>,
    otherEquipmentDetails: string
  ) => {
    if (!selectedEquipment["Other"]) {
      // just send back an empty array if none is selected
      return selectedEquipment["None"]
        ? []
        : Object.keys(selectedEquipment).filter(
            (key: any) =>
              selectedEquipment[key as keyof typeof selectedEquipment]
          );
    } else {
      const otherKeyRemoved = _.omit(selectedEquipment, "Other");
      return [
        ...Object.keys(otherKeyRemoved).filter(
          (key: any) => otherKeyRemoved[key as keyof typeof selectedEquipment]
        ),
        otherEquipmentDetails,
      ];
    }
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    setShowError(false);
    setErrorMessage("");
    try {
      const data = await createInitialDriverAccount({
        organizationId: organization?.id,
        firstName,
        lastName,
        avatar,
        email,
        phoneNumber,
        address,
        city,
        dateOfBirth: dateOfBirth.toDate(),
        province,
        postalCode: driverPostalCode,
        drivingLicenseNumber,
        drivingLicenseFrontUpload,
        drivingLicenseBackUpload,
        vehicleType,
        licensePlateNumber,
        makeOfCar,
        carColor,
        carYear: carYear.toString(),
        vehicleSideUpload,
        vehicleProofOfOwnershipUpload,
        vehicleInsuranceUpload,
        driverUsingOwnVehicle,
        driverUsingOrganizationVehicle,
        insurancePolicyNumber,
        selectedEquipment: formatSelectedEquipment(
          selectedEquipment,
          otherEquipmentDetails
        ),
      });
      setShowSuccess(true);
      resetFormFields();
      // resetBlurState();
    } catch (error: any) {
      setShowError(true);
      setErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  const pages = [
    {
      title: "Driver Registration form",
      submitButtonLabel: "Next",
      cancelButtonLabel: "Back",
      component: (
        <DriverProfileDetailsForm
          organization={organization}
          setOrganization={setOrganization}
          organizationSearchText={organizationSearchText}
          setOrganizationSearchText={setOrganizationSearchText}
          currentDate={currentDate}
          firstName={firstName}
          setFirstName={setFirstName}
          lastName={lastName}
          setLastName={setLastName}
          avatar={avatar}
          setAvatar={setAvatar}
          avatarUploadLoading={avatarUploadLoading}
          setAvatarUploadLoading={setAvatarUploadLoading}
          email={email}
          setEmail={setEmail}
          dateOfBirth={dateOfBirth}
          setDateOfBirth={setDateOfBirth}
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          address={address}
          setAddress={setAddress}
          city={city}
          setCity={setCity}
          province={province}
          setProvince={setProvince}
          driverPostalCode={driverPostalCode}
          setDriverPostalCode={setDriverPostalCode}
        />
      ),
      loading: creationValidationLoading,
      onSubmit: validateNewDriverCreate,
      handleBackPress: () => {
        setPage((prev) => prev - 1);
      },
      submitButtonDisabled:
        !organization?.id ||
        !validatePresence(firstName) ||
        !validatePresence(lastName) ||
        !validateEmail(email) ||
        !validateDateOfBirth(dateOfBirth) ||
        !validatePresence(avatar) ||
        !validatePhoneNumber(phoneNumber) ||
        !validatePresence(address) ||
        !validatePresence(city) ||
        !validatePresence(province) ||
        !validatePostalCode(driverPostalCode),
    },
    {
      title: "Driver Registration form",
      submitButtonLabel: "Next",
      cancelButtonLabel: "Back",
      component: (
        <DriverLicenseAndInsuranceNumberForm
          drivingLicenseNumber={drivingLicenseNumber}
          setDrivingLicenseNumber={setDrivingLicenseNumber}
          insurancePolicyNumber={insurancePolicyNumber}
          setInsurancePolicyNumber={setInsurancePolicyNumber}
          driverUsingOwnVehicle={driverUsingOwnVehicle}
          setDriverUsingOwnVehicle={setDriverUsingOwnVehicle}
          driverUsingOrganizationVehicle={driverUsingOrganizationVehicle}
          setDriverUsingOrganizationVehicle={setDriverUsingOrganizationVehicle}
        />
      ),
      loading: creationValidationLoading,
      onSubmit: () => {
        setPage((prev) => prev + 1);
      },
      handleBackPress: () => {
        setPage((prev) => prev - 1);
      },
      submitButtonDisabled:
        (!driverUsingOwnVehicle && !driverUsingOrganizationVehicle) ||
        !validatePresence(drivingLicenseNumber) ||
        (driverUsingOwnVehicle && !validatePresence(insurancePolicyNumber)),
    },
    {
      title: "Vehicle details",
      submitButtonLabel: "Next",
      cancelButtonLabel: "Back",
      component: (
        <DriverVehicleDetailsForm
          driverUsingOwnVehicle={driverUsingOwnVehicle}
          isRootOrg={organization?.isRootOrg}
          vehicleClassMappingsData={billingSettingsData?.vehicleRates}
          vehicleType={vehicleType}
          setVehicleType={setVehicleType}
          makeOfCar={makeOfCar}
          setMakeOfCar={setMakeOfCar}
          licensePlateNumber={licensePlateNumber}
          setLicensePlateNumber={setLicensePlateNumber}
          carColor={carColor}
          setCarColor={setCarColor}
          carYear={carYear}
          setCarYear={setCarYear}
          selectedEquipment={selectedEquipment}
          setSelectedEquipment={setSelectedEquipment}
          otherEquipmentDetails={otherEquipmentDetails}
          setOtherEquipmentDetails={setOtherEquipmentDetails}
        />
      ),
      loading: creationValidationLoading,
      onSubmit: () => {
        setPage((prev) => prev + 1);
      },
      handleBackPress: () => {
        setPage((prev) => prev - 1);
      },
      submitButtonDisabled:
        !validatePresence(vehicleType) ||
        (driverUsingOwnVehicle &&
          (!validatePresence(licensePlateNumber) ||
            !validatePresence(makeOfCar) ||
            !validatePresence(carColor) ||
            !Boolean(isAnyKeyValueTrue(selectedEquipment)) ||
            (selectedEquipment["Other"] &&
              !validatePresence(otherEquipmentDetails)))),
    },
    {
      title: "Document Upload",
      submitButtonLabel: "Next",
      cancelButtonLabel: "Back",
      component: (
        <DriverDocumentUploadForm
          driverUsingOwnVehicle={driverUsingOwnVehicle}
          drivingLicenseFrontUpload={drivingLicenseFrontUpload}
          setDrivingLicenseFrontUpload={setDrivingLicenseFrontUpload}
          drivingLicenseFrontUploadLoading={drivingLicenseFrontUploadLoading}
          setDrivingLicenseFrontUploadLoading={
            setDrivingLicenseFrontUploadLoading
          }
          drivingLicenseBackUpload={drivingLicenseBackUpload}
          setDrivingLicenseBackUpload={setDrivingLicenseBackUpload}
          drivingLicenseBackUploadLoading={drivingLicenseBackUploadLoading}
          setDrivingLicenseBackUploadLoading={
            setDrivingLicenseBackUploadLoading
          }
          vehicleSideUpload={vehicleSideUpload}
          setVehicleSideUpload={setVehicleSideUpload}
          vehicleSideUploadLoading={vehicleSideUploadLoading}
          setVehicleSideUploadLoading={setVehicleSideUploadLoading}
          vehicleProofOfOwnershipUpload={vehicleProofOfOwnershipUpload}
          setVehicleProofOfOwnershipUpload={setVehicleProofOfOwnershipUpload}
          vehicleProofOfOwnershipUploadLoading={
            vehicleProofOfOwnershipUploadLoading
          }
          setVehicleProofOfOwnershipUploadLoading={
            setVehicleProofOfOwnershipUploadLoading
          }
          vehicleInsuranceUpload={vehicleInsuranceUpload}
          setVehicleInsuranceUpload={setVehicleInsuranceUpload}
          vehicleInsuranceUploadLoading={vehicleInsuranceUploadLoading}
          setVehicleInsuranceUploadLoading={setVehicleInsuranceUploadLoading}
        />
      ),
      loading:
        drivingLicenseFrontUploadLoading ||
        drivingLicenseBackUploadLoading ||
        vehicleSideUploadLoading ||
        vehicleProofOfOwnershipUploadLoading ||
        vehicleInsuranceUploadLoading,
      onSubmit: () => {
        setPage((prev) => prev + 1);
      },
      handleBackPress: () => {
        setPage((prev) => prev - 1);
      },
      submitButtonDisabled: driverUsingOwnVehicle
        ? !validatePresence(drivingLicenseFrontUpload) ||
          !validatePresence(drivingLicenseBackUpload) ||
          !validatePresence(vehicleSideUpload) ||
          !validatePresence(vehicleProofOfOwnershipUpload) ||
          !validatePresence(vehicleInsuranceUpload)
        : !validatePresence(drivingLicenseFrontUpload) ||
          !validatePresence(drivingLicenseBackUpload),
    },
    {
      title: "Document Upload",
      submitButtonLabel: "Submit",
      cancelButtonLabel: "Back",
      component: (
        <CriminalRecordCheckConsentForm
          criminalRecordSubmitted={criminalRecordSubmitted}
          setCriminalRecordSubmitted={setCriminalRecordSubmitted}
        />
      ),
      loading: false,
      onSubmit: handleFinalSubmit,
      handleBackPress: () => {
        setPage((prev) => prev - 1);
      },
      submitButtonDisabled: !criminalRecordSubmitted,
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => {
          setShowSuccess(false);
        }}
      >
        <Alert
          onClose={() => {
            setShowSuccess(false);
          }}
          severity="success"
          sx={{ width: "100%" }}
        >
          Your account has been succesfully created. Please check your inbox for
          further instructions.
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={showError}
        autoHideDuration={6000}
        onClose={() => {
          setShowError(false);
          setErrorMessage("");
        }}
      >
        <Alert
          onClose={() => {
            setShowError(false);
            setErrorMessage("");
          }}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Container
        maxWidth={false}
        disableGutters
        sx={{ height: "100vh", backgroundColor: "#f5f5f5" }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            px: { xs: 4, sm: 8, md: "20%", lg: "25%" },
            py: 6,
            backgroundColor: { xs: "white", sm: "#f5f5f5" },
          }}
        >
          <Box
            sx={{
              backgroundColor: "white",
              padding: { xs: 0, sm: 4 },
              borderRadius: 3,
            }}
          >
            <Grid container spacing={2}>
              <Grid container item spacing={2}>
                <Grid item>
                  <Typography fontSize={20} fontWeight={700} pb={4}>
                    {pages[page].title}
                  </Typography>
                </Grid>
              </Grid>
              {pages[page]?.component}
              <Grid container item spacing={2}>
                {page > 0 && (
                  <Grid item xs={12} md={6}>
                    <Button
                      fullWidth
                      loading={pages[page].loading}
                      onClick={pages[page].handleBackPress}
                    >
                      {pages[page].cancelButtonLabel}
                    </Button>
                  </Grid>
                )}
                <Grid item xs={12} md={page === 0 ? 12 : 6}>
                  <Button
                    fullWidth
                    disabled={pages[page].submitButtonDisabled}
                    loading={pages[page].loading}
                    onClick={pages[page].onSubmit}
                  >
                    {pages[page].submitButtonLabel}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Container>
    </LocalizationProvider>
  );
};

export default DriverSignup;
